export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const headers = new Headers(init.headers);
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-ChanHub-Request', '1');
  const response = await fetch(input, { ...init, headers, credentials: 'same-origin', cache: 'no-store' });
  if (response.status === 401) {
    let target: Window = window;
    try { if (window.top?.location.origin === location.origin) target = window.top; } catch { /* externally embedded page keeps its own return address */ }
    const next = `${target.location.pathname}${target.location.search}${target.location.hash}`;
    if (target.location.pathname !== '/login') target.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
  return response;
}

export class HttpError extends Error {
  constructor(message: string, readonly status: number, readonly payload?: unknown) {
    super(message);
    this.name = 'HttpError';
  }
}

function describeDetail(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (Array.isArray(value)) {
    const messages = value.map((item) => {
      if (!item || typeof item !== 'object') return typeof item === 'string' ? item : undefined;
      const entry = item as { loc?: unknown[]; message?: unknown; msg?: unknown };
      const text = typeof entry.message === 'string' ? entry.message : typeof entry.msg === 'string' ? entry.msg : undefined;
      const location = Array.isArray(entry.loc) ? entry.loc.filter((part) => typeof part === 'string' || typeof part === 'number').join('.') : '';
      return text ? `${location ? `${location}：` : ''}${text}` : undefined;
    }).filter((message): message is string => Boolean(message));
    if (messages.length) return messages.join('；');
  }
  if (value && typeof value === 'object') {
    const record = value as { message?: unknown; msg?: unknown; error?: unknown };
    return describeDetail(record.message) || describeDetail(record.msg) || describeDetail(record.error);
  }
  return undefined;
}

export async function parseHttpError(response: Response): Promise<HttpError> {
  const payload: unknown = await response.json().catch(() => undefined);
  const record = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload as { detail?: unknown; message?: unknown } : {};
  const message = describeDetail(record.detail) || describeDetail(record.message) || `请求失败（${response.status}）`;
  return new HttpError(message, response.status, payload);
}

export async function requestJson<T>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await apiFetch(input, { ...init, headers });
  if (!response.ok) throw await parseHttpError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
